import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  CalendarData,
  CurrentUser,
  DashboardDetail,
  DashboardSummary,
  PanelEnvelope,
  SpotifyNowPlaying
} from "@silph-scope/shared";
import { apiFetch } from "./client";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => apiFetch<CurrentUser>("/api/me"), staleTime: 5 * 60_000 });
}

const REFRESH_MS: Record<string, number> = {
  "5m": 5 * 60_000,
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  daily: 24 * 60 * 60_000
};

export function useDashboards() {
  return useQuery({
    queryKey: ["dashboards"],
    queryFn: () => apiFetch<DashboardSummary[]>("/api/dashboards"),
    refetchInterval: REFRESH_MS["5m"]
  });
}

export function useDashboard(id: string) {
  return useQuery({
    queryKey: ["dashboard", id],
    queryFn: () => apiFetch<DashboardDetail>(`/api/dashboards/${id}`)
  });
}

export function usePanel<T>(dashboardId: string, panelId: string, refreshInterval: string) {
  return useQuery({
    queryKey: ["panel", dashboardId, panelId],
    queryFn: () => apiFetch<PanelEnvelope<T>>(`/api/dashboards/${dashboardId}/panels/${panelId}`),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: REFRESH_MS[refreshInterval] ?? REFRESH_MS["15m"]
  });
}

export function useSpotifyNowPlaying() {
  return useQuery({
    queryKey: ["spotify", "now-playing"],
    queryFn: () => apiFetch<SpotifyNowPlaying>("/api/spotify/now-playing"),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000
  });
}

export function useCalendar(search: string) {
  return useQuery({
    queryKey: ["panel", "calendar", "events", search],
    queryFn: () => apiFetch<PanelEnvelope<CalendarData>>(`/api/dashboards/calendar/panels/events?${search}`),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: REFRESH_MS["15m"]
  });
}
